import { Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import Layout from './components/Layout';
import Landing from './pages/Landing';
import Support from './pages/Support';
import Admin from './pages/Admin';
import Downloads from './pages/Downloads';
import Bots from './pages/Bots';
import Penrose from './pages/Penrose';
import PenroseCommands from './pages/PenroseCommands';
import WhatsNew from './pages/WhatsNew';
import Plugins from './pages/Plugins';
import PluginsDocs from './pages/PluginsDocs';
import AuthCallback from './pages/AuthCallback';
import DiscordLink from './pages/DiscordLink';
import CheckoutSuccess from './pages/CheckoutSuccess';
import Overlay from './pages/Overlay';
import Status from './pages/Status';
import NotFound from './pages/NotFound';
import { lazyPage } from './lib/lazyPage';
import { isReservedSlug } from './lib/reservedSlugs';
import { isVanityHost } from './lib/siteHost';

// Each lazy page that fails to load after a deploy says the site was updated,
// with a reload past the cache, instead of a blank screen (lib/lazyPage.tsx).
// The overlay builder is large (the full editor, its preview and chat feeds), so
// it loads only when someone opens /overlays.
const Overlays = lazyPage(() => import('./pages/Overlays'));
// A member's public profile, opened from shared links.
const Member = lazyPage(() => import('./pages/Member'));
// Where a member builds their page; a full-screen app of its own.
const Dashboard = lazyPage(() => import('./pages/Dashboard'));
// Seasonal event page, self-contained under src/events/knockKnock.
const KnockKnock = lazyPage(() => import('./pages/events/KnockKnock'));
// Vanity Studio: any paint and badge on any name, unreleased paints too; a
// full-screen tool of its own (src/vanity).
const Vanity = lazyPage(() => import('./pages/Vanity'));

// What a lazy page inside the site layout shows while its code loads: a
// screen of empty space, so the footer starts below the window instead of
// sitting mid-screen and jumping away when the page arrives.
const PAGE_HOLD = <div aria-hidden="true" className="min-h-dvh" />;

// A Twitch @, as functions/utils/publicProfile.ts VALID_LOGIN allows it (any case).
const LOGIN_SEGMENT = /^[A-Za-z0-9_]{1,25}$/;

/** streamnook.app/<login>: a member's profile at the root. Every page above is
 *  matched first; a word the site keeps, or anything that cannot be a Twitch @,
 *  is a plain 404. */
function RootProfile() {
  const { login = '' } = useParams();
  // streamnook.app/@name is streamnook.app/name.
  if (login.startsWith('@') && LOGIN_SEGMENT.test(login.slice(1)) && !isReservedSlug(login.slice(1))) {
    return <Navigate to={`/${login.slice(1).toLowerCase()}`} replace />;
  }
  if (!LOGIN_SEGMENT.test(login) || isReservedSlug(login)) return <NotFound />;
  return (
    <Suspense fallback={PAGE_HOLD}>
      <Member root />
    </Suspense>
  );
}

export default function App() {
  // vanity.streamnook.app (vanity.localhost while developing) is Vanity Studio
  // on every path, outside the site Layout; nothing else of the site renders there.
  if (isVanityHost(window.location.hostname)) {
    return (
      <Suspense fallback={null}>
        <Vanity />
      </Suspense>
    );
  }
  return (
    <Routes>
      {/* The OBS overlay renders chromeless + transparent, so it lives OUTSIDE the
          site Layout (no header/footer/background). */}
      <Route path="/overlay/:id" element={<Overlay />} />
      {/* The Halloween event is immersive: its own full-screen shell (slim header,
          seasonal theme, footer after the collection), outside the site Layout. */}
      <Route path="/events/knock-knock" element={<Suspense fallback={null}><KnockKnock /></Suspense>} />
      {/* The dashboard is a full-viewport shell with its own sidebar and logo,
          so it lives outside the site Layout too. */}
      <Route path="/dashboard/:section?" element={<Suspense fallback={null}><Dashboard /></Suspense>} />
      <Route element={<Layout />}>
        <Route path="/" element={<Landing />} />
        {/* The old account page lives on as sections of the dashboard. */}
        <Route path="/profile" element={<Navigate to="/dashboard" replace />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/account" element={<Navigate to="/dashboard" replace />} />
        <Route path="/downloads" element={<Downloads />} />
        <Route
          path="/overlays"
          element={
            <Suspense fallback={PAGE_HOLD}>
              <Overlays />
            </Suspense>
          }
        />
        <Route path="/bots" element={<Bots />} />
        <Route path="/penrose" element={<Penrose />} />
        <Route path="/penrose/commands" element={<PenroseCommands />} />
        <Route path="/whats-new" element={<WhatsNew />} />
        <Route path="/plugins" element={<Plugins />} />
        <Route path="/plugins/docs" element={<PluginsDocs />} />
        <Route path="/plugins/docs/:slug" element={<PluginsDocs />} />
        <Route path="/status" element={<Status />} />
        <Route
          path="/u/:login"
          element={
            <Suspense fallback={PAGE_HOLD}>
              <Member />
            </Suspense>
          }
        />
        <Route path="/support" element={<Support />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/discord/link" element={<DiscordLink />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/checkout/cancel" element={<Navigate to="/support" replace />} />
        <Route path="/:login" element={<RootProfile />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
